import { useEffect, useRef, useState } from "react";
import type { CrowdReaction } from "../../game/emotion-model.js";

/**
 * The three Vet-Day crowds as they sit on the Brief screen, left → right:
 *   Pet Parents · Rescue Warriors (SERVED, middle) · Eat the Rich.
 * The middle crowd is the audience we're cutting for, so it reads emphasized
 * (slightly larger + a ring in the crowd's pink accent).
 */
export interface HeaderFace {
  key: string;
  label: string;
  accent: string;
  served: boolean;
  reaction: CrowdReaction;
}

/**
 * Map a live crowd reaction to a compact reaction face. Presentation only —
 * driven by `crowdReaction` (fit / backlash / inRed), never by scoring.
 *   in-red / backlash → upset · in-green → happy · warming → light · else neutral.
 */
export function crowdFaceGlyph(reaction: CrowdReaction): string {
  if (reaction.inRed) return "😠";
  if (reaction.fit >= 0.5) return "😄";
  if (reaction.fit >= 0.2) return "🙂";
  return "😐";
}

/**
 * Signed "how this crowd is doing" reading, used only to detect movement between
 * edits: higher = more in their green, lower = more into their red. Presentation
 * only; never feeds scoring.
 */
function reactionNet(r: CrowdReaction): number {
  return r.fit - r.backlash;
}

/** A crowd must move at least this much (net) before we pop a +/- feedback cue. */
const POP_THRESHOLD = 0.08;

/** A live +/- feedback pop over one crowd face. `id` re-triggers the animation. */
interface FacePop {
  dir: "plus" | "minus";
  id: number;
}

/**
 * The three reaction faces shown inside the editor's LVL pill. Order + served
 * emphasis mirror the Brief screen so the pill reads as the same three crowds.
 *
 * When an edit moves a crowd's reaction beyond a small threshold, a floating
 * "+" (moved toward their green) or "-" (moved toward their red) briefly pops
 * over THAT crowd's face. Debounced so tiny changes do not spam. Presentation
 * only — driven by the change in each crowd's `crowdReaction`, never scoring.
 */
export function EditorHeaderFaces({ faces }: { faces: HeaderFace[] }) {
  const prevNet = useRef<Record<string, number> | null>(null);
  const popSeq = useRef(0);
  const [pops, setPops] = useState<Record<string, FacePop>>({});

  useEffect(() => {
    const nextNet: Record<string, number> = {};
    for (const f of faces) nextNet[f.key] = reactionNet(f.reaction);

    // First render just seeds the baseline — never pop on mount.
    if (prevNet.current) {
      const fresh: Record<string, FacePop> = {};
      for (const f of faces) {
        const before = prevNet.current[f.key];
        if (before == null) continue;
        const delta = (nextNet[f.key] ?? before) - before;
        if (Math.abs(delta) >= POP_THRESHOLD) {
          fresh[f.key] = { dir: delta > 0 ? "plus" : "minus", id: ++popSeq.current };
        }
      }
      if (Object.keys(fresh).length > 0) {
        setPops((cur) => ({ ...cur, ...fresh }));
      }
    }
    prevNet.current = nextNet;
  }, [faces]);

  // Retire each pop ~0.8s after it fires (matches the CSS float-up + fade).
  useEffect(() => {
    const ids = Object.keys(pops);
    if (ids.length === 0) return;
    const timers = ids.map((key) => {
      const seq = pops[key]!.id;
      return window.setTimeout(() => {
        setPops((cur) => {
          if (cur[key]?.id !== seq) return cur;
          const rest = { ...cur };
          delete rest[key];
          return rest;
        });
      }, 820);
    });
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [pops]);

  return (
    <span className="editor-level-faces" aria-hidden>
      {faces.map((f) => {
        const pop = pops[f.key];
        return (
          <span
            key={f.key}
            className={`elp-face${f.served ? " elp-face-served" : ""}`}
            style={{ ["--crowd-accent" as string]: f.accent }}
            data-testid={`level-face-${f.key}`}
            data-reaction={
              f.reaction.inRed ? "red" : f.reaction.fit >= 0.2 ? "green" : "neutral"
            }
          >
            {crowdFaceGlyph(f.reaction)}
            {pop ? (
              <span
                key={pop.id}
                className={`elp-face-pop elp-face-pop-${pop.dir}`}
                data-testid={`level-face-pop-${f.key}`}
                data-dir={pop.dir}
              >
                {pop.dir === "plus" ? "+" : "-"}
              </span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}
